import React, { useEffect, useState } from 'react';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaDownload } from 'react-icons/fa';
import ActionDropdown from './ActionDropdown';
import { Formatter } from '../format';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

// Wraps a plain <audio> element (kept off-screen, no native `controls`) with a
// UI that matches the rest of the app instead of the browser's own player —
// the native one carries its own volume/overflow-menu chrome that can't be
// restyled. All playback state here just mirrors the underlying element's
// real events, so external control (e.g. clicking a transcript word to seek
// via the same audioRef) keeps working unchanged.
const AudioPlayer = ({ audioRef, src, onTimeUpdate, onLoadedMetadata }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleLoaded = () => setDuration(audio.duration || 0);
    const handleTime = () => setCurrentTime(audio.currentTime);
    const handleVolume = () => setMuted(audio.muted);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadedmetadata', handleLoaded);
    audio.addEventListener('timeupdate', handleTime);
    audio.addEventListener('volumechange', handleVolume);
    if (audio.duration) setDuration(audio.duration);
    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadedmetadata', handleLoaded);
      audio.removeEventListener('timeupdate', handleTime);
      audio.removeEventListener('volumechange', handleVolume);
    };
  }, [audioRef, src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play();
    else audio.pause();
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (audio) audio.muted = !audio.muted;
  };

  const setPlaybackRate = (r) => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = r;
    setRate(r);
  };

  const seek = (e) => {
    const t = parseFloat(e.target.value);
    const audio = audioRef.current;
    if (audio) audio.currentTime = t;
    setCurrentTime(t);
  };

  return (
    <div className="flex items-center gap-3 flex-1 min-w-[240px]">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        className="hidden"
      >
        Your browser does not support the audio element.
      </audio>

      <button
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        className="flex-shrink-0 w-9 h-9 rounded-full bg-cloudza-600 hover:bg-cloudza-700 text-white flex items-center justify-center transition-colors"
      >
        {isPlaying ? <FaPause className="text-xs" /> : <FaPlay className="text-xs ml-0.5" />}
      </button>

      <span className="text-xs text-gray-500 tabular-nums flex-shrink-0">
        {Formatter.Time(currentTime)} / {Formatter.Time(duration)}
      </span>

      <input
        type="range"
        min={0}
        max={duration || 0}
        step="0.1"
        value={Math.min(currentTime, duration || 0)}
        onChange={seek}
        className="flex-1 min-w-[80px] accent-cloudza-600 h-1.5 cursor-pointer"
        aria-label="Seek"
      />

      <button
        onClick={toggleMute}
        aria-label={muted ? 'Unmute' : 'Mute'}
        className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-cloudza-600 hover:bg-cloudza-50 transition-colors"
      >
        {muted ? <FaVolumeMute className="text-sm" /> : <FaVolumeUp className="text-sm" />}
      </button>

      <ActionDropdown
        label={`${rate}x`}
        className="flex-shrink-0"
        actions={SPEEDS.map((s) => ({
          label: `${s}x${s === 1 ? ' (Normal)' : ''}`,
          onClick: () => setPlaybackRate(s),
        }))}
      />

      <a
        href={src}
        download
        aria-label="Download audio"
        className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-cloudza-600 hover:bg-cloudza-50 transition-colors"
      >
        <FaDownload className="text-sm" />
      </a>
    </div>
  );
};

export default AudioPlayer;
